import { NullValue } from './struct.enums.js';

export function nullValueFromJSON(object: any): NullValue {
  switch (object) {
    case 0:
    case "NULL_VALUE":
      return NullValue.NULL_VALUE;
    case -1:
    case "UNRECOGNIZED":
    default:
      return NullValue.UNRECOGNIZED;
  }
}

export function nullValueToJSON(object: NullValue): string {
  switch (object) {
    case NullValue.NULL_VALUE:
      return "NULL_VALUE";
    default:
      return "UNKNOWN";
  }
}

/**
 * `Struct` represents a structured data value, consisting of fields
 * which map to dynamically typed values. In some languages, `Struct`
 * might be supported by a native representation. For example, in
 * scripting languages like JS a struct is represented as an
 * object. The details of that representation are described together
 * with the proto support for the language.
 *
 * The JSON representation for `Struct` is JSON object.
 */
export interface Struct {
  /** An unordered map of dynamically typed values. */
  fields: { [key: string]: Value };
}

export interface Struct_FieldsEntry {
  key: string;
  value?: Value;
}

/**
 * `Value` represents a dynamically typed value which can be either
 * null, a number, a string, a boolean, a recursive struct value, or a
 * list of values. A producer of value is expected to set one of these
 * variants. Absence of any variant indicates an error.
 *
 * The JSON representation for `Value` is JSON value.
 */
export interface Value {
  /** A null value. */
  nullValue: NullValue | undefined;
  /** A double value. */
  numberValue: number | undefined;
  /** A string value. */
  stringValue: string | undefined;
  /** A boolean value. */
  boolValue: boolean | undefined;
  /** A structured value. */
  structValue?: Struct | undefined;
  /** An array of `value` objects. */
  listValue?: ListValue | undefined;
}

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values: Value[];
}

const baseStruct: object = {};

export const Struct = {
  fromJSON(object: any): Struct {
    const message = { ...baseStruct } as Struct;
    message.fields = Object.entries(object.fields ?? {}).reduce<{
      [key: string]: Value;
    }>((acc, [key, value]) => {
      acc[key] = Value.fromJSON(value);
      return acc;
    }, {});
    return message;
  },

  toJSON(message: Struct): unknown {
    const obj: any = {};
    obj.fields = {};
    if (message.fields) {
      Object.entries(message.fields).forEach(([k, v]) => {
        obj.fields[k] = Value.toJSON(v);
      });
    }
    return obj;
  },
};

const baseStruct_FieldsEntry: object = { key: "" };

export const Struct_FieldsEntry = {
  fromJSON(object: any): Struct_FieldsEntry {
    const message = { ...baseStruct_FieldsEntry } as Struct_FieldsEntry;
    message.key =
      object.key !== undefined && object.key !== null ? String(object.key) : "";
    message.value =
      object.value !== undefined && object.value !== null
        ? Value.fromJSON(object.value)
        : undefined;
    return message;
  },

  toJSON(message: Struct_FieldsEntry): unknown {
    const obj: any = {};
    message.key !== undefined && (obj.key = message.key);
    message.value !== undefined &&
      (obj.value = message.value ? Value.toJSON(message.value) : undefined);
    return obj;
  },
};

const baseValue: object = {};

export const Value = {
  fromJSON(object: any): Value {
    const message = { ...baseValue } as Value;
    message.nullValue =
      object.nullValue !== undefined && object.nullValue !== null
        ? nullValueFromJSON(object.nullValue)
        : undefined;
    message.numberValue =
      object.numberValue !== undefined && object.numberValue !== null
        ? Number(object.numberValue)
        : undefined;
    message.stringValue =
      object.stringValue !== undefined && object.stringValue !== null
        ? String(object.stringValue)
        : undefined;
    message.boolValue =
      object.boolValue !== undefined && object.boolValue !== null
        ? Boolean(object.boolValue)
        : undefined;
    message.structValue =
      object.structValue !== undefined && object.structValue !== null
        ? Struct.fromJSON(object.structValue)
        : undefined;
    message.listValue =
      object.listValue !== undefined && object.listValue !== null
        ? ListValue.fromJSON(object.listValue)
        : undefined;
    return message;
  },

  toJSON(message: Value): unknown {
    const obj: any = {};
    message.nullValue !== undefined &&
      (obj.nullValue =
        message.nullValue !== undefined
          ? nullValueToJSON(message.nullValue)
          : undefined);
    message.numberValue !== undefined &&
      (obj.numberValue = message.numberValue);
    message.stringValue !== undefined &&
      (obj.stringValue = message.stringValue);
    message.boolValue !== undefined && (obj.boolValue = message.boolValue);
    message.structValue !== undefined &&
      (obj.structValue = message.structValue
        ? Struct.toJSON(message.structValue)
        : undefined);
    message.listValue !== undefined &&
      (obj.listValue = message.listValue
        ? ListValue.toJSON(message.listValue)
        : undefined);
    return obj;
  },
};

const baseListValue: object = {};

export const ListValue = {
  fromJSON(object: any): ListValue {
    const message = { ...baseListValue } as ListValue;
    message.values = (object.values ?? []).map((e: any) => Value.fromJSON(e));
    return message;
  },

  toJSON(message: ListValue): unknown {
    const obj: any = {};
    if (message.values) {
      obj.values = message.values.map((e) => (e ? Value.toJSON(e) : undefined));
    } else {
      obj.values = [];
    }
    return obj;
  },
};
